/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 lab ToggleButton + ToggleButtonGroup — NS/Tractie-spirit overrides.
//
// Target classKeys (ToggleButton): root, disabled, selected, label, sizeSmall,
//   sizeLarge. (ButtonBase contributes `Mui-focusVisible` to the root element
//   at runtime — targeted via `&.Mui-focusVisible` compound selector.)
// Target classKeys (ToggleButtonGroup): root, vertical, grouped,
//   groupedHorizontal, groupedVertical.
//
// Architectural call: no native Tractie pendant (TractieToggle is a binary
// switch, not segmented). Override derives Tractie-Button-spirit:
//   - Base: 6px radius, 700 weight, no transition, focus formula.
//   - Selected: Tractie selected-default tokens.
//   - Group: collapsed shared border between adjacent segments.
//
// Pseudo-state forcing (foundation T1.3): comma-separated `&.force-*` variants
// allow comparison-grid stories to render hover/active/focus-visible rows.

// ToggleButton focus formula. Outline-offset is verhoogd naar 2px (i.p.v. de
// Button-family default 1px) zodat in grouped+selected context het focus-ring
// visueel duidelijk gescheiden blijft van de segment-border. Beide gebruiken
// `--ns-color-border-focus` als kleur — zonder voldoende offset blenden ze.
const focusVisibleOnRoot = {
  outline: 'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
  outlineOffset: '2px',
  boxShadow: 'var(--ns-shadow-elevated-1)',
} as const;

export const MuiToggleButtonLightOverrides = {
  root: {
    boxSizing: 'border-box' as const,
    fontFamily: 'var(--ns-font-family-regular)',
    fontSize: 'var(--ns-font-size-m)',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: 'normal',
    minWidth: 40,
    minHeight: 40,
    textTransform: 'none' as const,
    borderRadius: 6,
    border: '1px solid var(--ns-color-border-interactive-default)',
    color: 'var(--ns-color-fg-default)',
    padding: '8px 16px',
    transition: 'none',
    backgroundColor: 'transparent',

    // Hover — Tractie hover token. Force-class variant mirrors :hover.
    '&:hover, &.force-hover': {
      backgroundColor: 'var(--ns-color-bg-hover)',
      '&$disabled': {
        backgroundColor: 'transparent',
      },
    },

    // Active/pressed — Tractie pressed token.
    '&:active, &.force-active': {
      backgroundColor: 'var(--ns-color-bg-pressed)',
    },

    // Focus-visible — Tractie Button focus formula. `Mui-focusVisible` is the
    // ButtonBase-applied state class; force variant for stories.
    '&.Mui-focusVisible, &.force-focus-visible': {
      ...focusVisibleOnRoot,
    },

    // Selected — Tractie selected-default tokens. `&$selected` targets the
    // `selected` classKey below; force variant for stories.
    '&$selected, &.force-selected': {
      backgroundColor: 'var(--ns-color-bg-selected-default)',
      borderColor: 'var(--ns-color-bg-selected-default)',
      color: 'var(--ns-color-fg-default)',
      '&:hover, &.force-hover': {
        backgroundColor: 'var(--ns-color-bg-selected-hover)',
      },
      '&:active, &.force-active': {
        backgroundColor: 'var(--ns-color-bg-selected-pressed)',
      },
    },

    // Selected in grouped context: borderColor moet `interactive-default`
    // blijven zodat de group's outer outline continu blijft rondom segments.
    // Specificity-bump via 3 classes (root + groupedHorizontal/Vertical +
    // Mui-selected) wint van de generieke `&$selected` op root.
    '&.MuiToggleButtonGroup-groupedHorizontal.Mui-selected, &.MuiToggleButtonGroup-groupedHorizontal.force-selected, &.MuiToggleButtonGroup-groupedVertical.Mui-selected, &.MuiToggleButtonGroup-groupedVertical.force-selected':
      {
        borderColor: 'var(--ns-color-border-interactive-default)',
      },

    // Disabled — Tractie Button-family opacity (0.25 per existing Button spec).
    '&$disabled': {
      color: 'var(--ns-color-fg-default)',
      opacity: 0.25,
      borderColor: 'var(--ns-color-border-interactive-default)',
    },

    // Icon-only — Tractie iconOnly square (40×40 m / 32×32 s). Story zet
    // `data-icon-only=""` voor C3 cluster. Spiegel-patroon van Button overrides.
    '&[data-icon-only]': {
      minWidth: 40,
      width: 40,
      height: 40,
      padding: 0,
      '&$sizeSmall': {
        minWidth: 32,
        width: 32,
        height: 32,
        padding: 0,
      },
    },
  },

  sizeSmall: {
    minWidth: 32,
    minHeight: 32,
    fontSize: 'var(--ns-font-size-s)',
    padding: '7px 15px',
  },

  // Empty hooks needed so `&$selected` / `&$disabled` resolve to the JSS
  // generated class names. (MUI v4 JSS-overrides pattern.)
  selected: {},
  disabled: {},

  label: {
    width: '100%',
    display: 'inherit' as const,
    alignItems: 'inherit' as const,
    justifyContent: 'inherit' as const,
  },
} as const;

export const MuiToggleButtonGroupLightOverrides = {
  root: {
    display: 'inline-flex' as const,
    alignItems: 'stretch' as const,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: 6,
  },

  // `grouped` matches both horizontal & vertical groupings.
  grouped: {
    '&:not(:first-child)': {
      borderLeftWidth: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      marginLeft: 0,
    },
    '&:not(:last-child)': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },

  // `groupedHorizontal` has higher MUI-internal specificity than `grouped`.
  // Without this override, MUI's own `-1px marginLeft + 1px border` collapse
  // leaves a faint double-stroke at the segment seam. Setting borderLeftWidth
  // to 0 plus zero margin yields a clean shared border between segments.
  groupedHorizontal: {
    '&:not(:first-child)': {
      borderLeftWidth: 0,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      marginLeft: 0,
    },
    '&:not(:last-child)': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    // Selected segment in group: behoud border-color = interactive-default
    // (matched non-selected segments) zodat de outer group-outline continu blijft.
    // Solo selected (C2) blijft de "filled pill" look van Chip houden via
    // de `root.&$selected` rule die borderColor = bg-color zet — die wordt
    // door deze nested groupedHorizontal-rule overruled in grouped context.
    // Bewust geen `&$selected` JSS-ref: het Group-overrides-object heeft geen
    // `selected` key (die zit op MuiToggleButton), dus JSS zou een unresolved
    // reference warning loggen. `.Mui-selected` is de native MUI runtime-class.
    '&.Mui-selected, &.force-selected': {
      borderColor: 'var(--ns-color-border-interactive-default)',
    },
  },
} as const;

// Dark-mode reuses the Light overrides; all colors flow through CSS custom
// properties that swap on `[data-theme-mode='dark']` via tokens.css.
export const MuiToggleButtonDarkOverrides = MuiToggleButtonLightOverrides;
export const MuiToggleButtonGroupDarkOverrides =
  MuiToggleButtonGroupLightOverrides;
